import classNames from 'classnames';
import type { CSSProperties, RefObject } from 'react';
import React, { use, useRef, useState } from 'react';

import { ClubSearchLink } from 'src/components/common/club-search/club-search-link';
import { SiteContext } from 'src/context/site-context';
import { useClubSearch } from 'src/custom-hooks/use-club-search-form';
import useOutsideClick from 'src/custom-hooks/use-outside-click';
import useWindowSize from 'src/custom-hooks/use-window-size';
import {
  amplitudeBrandName,
  amplitudeButtonTrackEvent,
  amplitudeTrackEvent,
} from 'src/helpers/amplitude-event-track';

import Cross from '../../icons/cross';
import Map from '../../icons/map';
import Search from '../../icons/search-icon';

import * as styles from './club-search.module.scss';

type ClubSearchInputProps = {
  inputPlaceholder: string;
  pinnedSearchOptionText: string;
  inputRef?: RefObject<HTMLInputElement | null>;
  isNavigation?: boolean;
  onFinish?: () => void;
};

export const ClubSearchInput = ({
  inputPlaceholder,
  pinnedSearchOptionText,
  inputRef,
  isNavigation,
  onFinish,
}: ClubSearchInputProps) => {
  const containerRef = useRef(null);
  const { styling, pageLevel, buildType, language } = use(SiteContext);
  const [searchValue, setSearchValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { isTabletOrSmaller } = useWindowSize();
  const filteredClubs = useClubSearch(searchValue);
  const dropdownStyle = {
    '--club-search-dropdown-height': `${
      isDropdownOpen ? Math.min((filteredClubs.length + 1) * 3.75 + 1, 31) : 0
    }rem`,
  } as CSSProperties;

  useOutsideClick(containerRef, () => {
    if (!isTabletOrSmaller) {
      setIsDropdownOpen(false);
    }
  });

  const clearButton = (
    <div
      tabIndex={0}
      onClick={() => {
        setIsDropdownOpen(false);
        setSearchValue('');
        inputRef?.current?.focus();
      }}
      className={classNames(styles.clearButton, searchValue.length > 0 && styles.shown)}
    >
      <Cross />
    </div>
  );

  const handleClickViewMap = () => {
    void amplitudeButtonTrackEvent({
      buildType,
      language,
      button: null,
      componentName: 'Club Search',
      pageLevel,
      ctaName: `View Map in the "Find a club" - ${isNavigation ? 'Navigation' : 'On The Page'}`,
    });
  };

  const handleInputFocus = () =>
    amplitudeTrackEvent(`Focus Input`, {
      custom_page: window.location.origin,
      custom_level: pageLevel,
      brand: amplitudeBrandName(buildType),
      buildType,
      language,
      ctaName: `Focus to Club List in the "Find a club" - ${isNavigation ? 'Navigation' : 'On The Page'}`,
      component: 'Club Search',
    });

  const viewMapLink = (
    <button
      type="submit"
      className={classNames(styles.viewMapButton, isDropdownOpen && styles.open)}
      onClick={handleClickViewMap}
    >
      <>
        <div className={styles.viewMapContent}>
          <Map />
          {pinnedSearchOptionText}
        </div>

        {!isTabletOrSmaller && (
          <span
            className={classNames(
              styles.viewMapDivider,
              filteredClubs.length > 0 && styles.visible
            )}
          />
        )}
      </>
    </button>
  );

  return (
    <div ref={containerRef} className={styles.inputContainer}>
      <div className={styles.inputWrapper}>
        {clearButton}
        <div className={styles.searchIcon}>
          <Search fill={styling.colors.capeCod} />
        </div>
        <input
          ref={inputRef}
          value={searchValue}
          onChange={(e) => {
            const { value } = e.currentTarget;
            setIsDropdownOpen(value.trimStart().length > 0);
            setSearchValue(value.trimStart());
          }}
          onFocus={() => {
            setIsDropdownOpen(searchValue.length > 0);
            void handleInputFocus();
          }}
          name="club-search"
          autoComplete="off"
          autoCorrect="off"
          enterKeyHint="search"
          placeholder={inputPlaceholder}
          className={classNames(
            styles.input,
            isDropdownOpen && !isTabletOrSmaller && styles.dropdownOpen
          )}
          css={[styling.redesignForms.input]}
        />
      </div>
      <div
        className={classNames(styles.dropdown, isDropdownOpen && styles.open)}
        aria-hidden={!isDropdownOpen}
        style={dropdownStyle}
      >
        {viewMapLink}
        {filteredClubs.map((club) => (
          <ClubSearchLink
            key={`${club.siteId}-${searchValue}`}
            club={club}
            isOpen={isDropdownOpen}
            onClick={onFinish}
          />
        ))}
      </div>
    </div>
  );
};
